import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import Valuation from '../../Data/Valuation';
import Employee from '../../Data/Employee';
import EmployeeRow from './Employees/EmployeeRow';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import formatCurrency from './Helpers/FormatCurrency';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import MarkComplete from '../../Components/Forms/MarkComplete';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';

interface Props {
	valuation: Valuation;
	employees: Employee[];
}

interface FormData {
	employees: Partial<Employee>[];
	mark_complete: boolean;
}

const Employees = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post, processing } = useForm<FormData>({
		employees: replaceNullWithEmpty(props.employees) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('employees') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('employees', props.valuation.id, data);

	useEffect(() => {
		if (data.employees.length == 0) {
			addRow();
		}
	}, [props.employees]);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/employees`);
	};

	const addRow = () => {
		setData({
			...data,
			employees: [
				...data.employees,
				{
					id: '',
					first_name: '',
					last_name: '',
					agency_job_title: '',
					role: '',
					primary_function: '',
					birth_date: '',
					hire_date: '',
					years_of_experience: '',
					termination_date: '',
					termination_reason: '',
					avg_weekly_hours_worked: '',
					base_salary: '',
					commission: '',
					annual_bonus: '',
					percent_of_ownership: '',
					designation_1: '',
					designation_2: '',
					perpetuation_plan: false,
					gender: '',
					ethnicity: '',
				},
			] as Employee[],
		});
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData: any = { ...data };
		newData.employees[index][event.target.name] = getEventValue(event);
		setData(newData);
	};

	const deleteIndex = (index: number) => {
		const newData: any = { ...data };
		newData.employees.splice(index, 1);
		setData(newData);
	};

	// Values entered through the form are strings; coerce before summing so
	// `accumulator + value` adds instead of concatenating.
	const num = (v: any) => Number(v) || 0;

	const totalBaseSalary = formatCurrency(
		data.employees.reduce(
			(accumulator, currentValue) => accumulator + num(currentValue?.base_salary),
			0
		)
	);

	const totalCommission = formatCurrency(
		data.employees.reduce(
			(accumulator, currentValue) => accumulator + num(currentValue?.commission),
			0
		)
	);

	const totalAnnualBonus = formatCurrency(
		data.employees.reduce(
			(accumulator, currentValue) => accumulator + num(currentValue?.annual_bonus),
			0
		)
	);

	const totalTotalCompensation = formatCurrency(
		data.employees.reduce(
			(accumulator, currentValue) =>
				accumulator +
				num(currentValue?.base_salary) +
				num(currentValue?.commission) +
				num(currentValue?.annual_bonus),
			0
		)
	);

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Employees'
			/>
			<InstructionsPanel>
				Use this tab to record all owners and employees and their information. Press 'Add Row' as needed to add more employee information.
			</InstructionsPanel>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<div className='employees-scroll'>
				<Table striped borderless hover>
					<thead>
						<tr>
							<th
								id='employeeNum'
								className='cell-xs sticky-cell sticky-col-1'
							>
								#
							</th>
							<th id='first_name' className='sticky-cell sticky-col-2'>First Name</th>
							<th id='last_name' className='sticky-cell sticky-col-3'>Last Name</th>
							<th id='agency_job_title'>Agency Job Title</th>
							<th id='role' className='col-select-wide'>Role</th>
							<th id='primary_function' className='col-select-wide'>Primary Function</th>
							<th id='birth_date'>Birth Date</th>
							<th id='hire_date'>Hire Date</th>
							<th id='years_of_experience'>
								Years of Experience
							</th>
							<th id='termination_date'>Termination Date</th>
							<th id='termination_reason'>Termination reason</th>
							<th id='avg_weekly_hours_worked'>
								Avg Hours Worked Per Week
							</th>
							<th id='base_salary'>Base Salary</th>
							<th id='commission'>Commission</th>
							<th id='annual_bonus'>Annual Bonus</th>
							<th id='total_compensation'>Total Compensation</th>
							<th id='percent_of_ownership' className='cell-lg'>
								Percent of Ownership
							</th>
							<th id='designation_1'>Designation 1</th>
							<th id='designation_2'>Designation 2</th>
							<th id='perpetuation_plan'>Perpetuation Plan</th>
							<th id='gender'>Gender</th>
							<th id='ethnicity'>Race/Ethnicity</th>
							<th id='delete_element'></th>
						</tr>
					</thead>
					<tbody>
						{data.employees.map(
							(employee: Partial<Employee>, index: number) => (
								<EmployeeRow
									key={`employee-${index}`}
									employee={employee}
									index={index}
									errors={errors}
									updateIndex={updateIndex}
									deleteIndex={deleteIndex}
								/>
							)
						)}
					</tbody>
					<tfoot>
						<tr>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th>{totalBaseSalary}</th>
							<th>{totalCommission}</th>
							<th>{totalAnnualBonus}</th>
							<th>{totalTotalCompensation}</th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
							<th></th>
						</tr>
					</tfoot>
				</Table>
				</div>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/ownership`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{!readOnly && (
							<Button onClick={addRow} className='mt-4 ms-4'>
								Add Row
							</Button>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/accounts`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

Employees.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default Employees;
